import React, { PropsWithRef } from 'react';
import { useIntl } from 'react-intl';
import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightAndArrowDownLeftFromCenter';
import { faClosedCaptioning } from '@fortawesome/pro-regular-svg-icons/faClosedCaptioning';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faKeyboard } from '@fortawesome/pro-regular-svg-icons/faKeyboard';
import { faPause } from '@fortawesome/pro-regular-svg-icons/faPause';
import { faGaugeHigh } from '@fortawesome/pro-regular-svg-icons/faGaugeHigh';
import { faGaugeLow } from '@fortawesome/pro-regular-svg-icons/faGaugeLow';
import { faGaugeMax } from '@fortawesome/pro-regular-svg-icons/faGaugeMax';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faArrowDownLeftAndArrowUpRightToCenter } from '@fortawesome/pro-regular-svg-icons/faArrowDownLeftAndArrowUpRightToCenter';
import { faGaugeMed } from '@fortawesome/pro-regular-svg-icons/faGaugeMed';
import { faThList } from '@fortawesome/pro-regular-svg-icons/faThList';
import { faClone } from '@fortawesome/pro-regular-svg-icons/faClone';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { BoxProps } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { bg1 } from '@osmosis/tailwind-ui/styles/background';
import { CaptionSource } from '@osmosis/shared/types/learn';
import { VideoChapter } from '@osmosis/shared/types/learndb/learnData';
import { formatMinutesSeconds } from '@osmosis/shared/time';

import VideoProgressBar from './VideoProgressBar';
import ChapterBrowser from './ChapterBrowser';
import KeyboardShortcuts from './KeyboardShortcuts';
import PlaybackSpeedSelector from './PlaybackSpeedSelector';
import CaptionSelect from './CaptionSelect';
import VideoControlButton from './VideoControlButton';
import VolumeControls from './VolumeControls';

import SkipForward from '../icons/SkipForward';
import SkipBackward from '../icons/SkipBackward';

import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import { VideoEnum } from '../../types/video-player';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { OaiPlacement } from '@osmosis/shared/constants/learnAbTests';
import OsmosisAIButton from '../ai/OsmosisAIButton';
import { useRailAlwaysOpen } from '../ai/RailLayoutContext';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

const mdContentWidth = 756;
const smContentWidth = 570;

const isMD = (contentWidth: number) =>
  contentWidth ? contentWidth >= mdContentWidth : true;
const isSM = (contentWidth: number) =>
  contentWidth ? contentWidth >= smContentWidth : true;

const ResponsiveBox = (props: PropsWithRef<BoxProps>) => {
  const { contentWidth } = useContentSize();
  return isSM(contentWidth) ? <Box {...props} /> : null;
};

const ResponsiveText = (props: React.ComponentProps<typeof P>) => {
  const { contentWidth } = useContentSize();
  return (
    <P
      className={`whitespace-nowrap ${isSM(contentWidth) ? 'text-sm' : '!text-xs'} ${
        props.cn || ''
      }`}
      {...props}
    />
  );
};

export type VideoControlBarProps = {
  readonly captions?: CaptionSource[];
  readonly chapters?: VideoChapter[];
  readonly showOAIButton?: boolean;
  readonly className?: string;
};

/**
 * Control bar for video.
 */
export default function VideoControlBar({
  captions,
  chapters,
  showOAIButton,
  className,
}: VideoControlBarProps): JSX.Element {
  const {
    getPlayer,
    playing,
    handleSeekTo,
    isFullscreen,
    shortcuts,
    playbackRate,
    setPlaybackRate,
    activeCaptions,
    setActiveCaptions,
    handlePlayPause,
    handlePictureInPicture,
    handleFullscreen,
    type,
    progressState,
  } = useVideoPlayer();

  const player = getPlayer();

  const intl = useIntl();

  const { getClassValue } = useContentSize();
  // On the desktop learn rail the panel is always open, so the button would have nothing to do.
  const railAlwaysOpen = useRailAlwaysOpen();
  const oaiContainerClass = getClassValue({
    defaultClasses: 'max-[756px]:!w-9 w-[120px] sm:w-36 lg:w-40',
    callback: ({ contentWidth, sm, lg }) =>
      !isMD(contentWidth) ? 'w-9' : lg ? 'w-40' : sm ? 'w-36' : 'w-[120px]',
  });
  const oaiButtonClass = getClassValue({
    defaultClasses: cn(
      'border-none h-7 md:h-9',
      'max-[756px]:!px-0 px-0 sm:px-0.5',
      'max-sm:[&>p]:!text-xs max-lg:[&>p]:!text-sm',
    ),
    breakpoints: {
      lg: cn('border-none h-9 px-0.5'),
      md: cn('border-none h-9 px-0.5 [&>p]:!text-sm'),
      sm: cn('border-none h-7 px-0.5 [&>p]:!text-sm'),
      base: cn('border-none h-7 px-0 [&>p]:!text-xs'),
    },
  });
  const oaiTextClass = getClassValue({
    defaultClasses: 'max-[756px]:!hidden',
    callback: ({ contentWidth }) => (!isMD(contentWidth) ? 'hidden' : ''),
  });
  const oaiIconClass = getClassValue({
    defaultClasses: cn('mr-1 max-[756px]:!mr-0', text0),
    callback: ({ contentWidth }) =>
      cn(!isMD(contentWidth) ? 'mr-0' : 'mr-1', text0),
  });
  const leftControlsClass = getClassValue({
    defaultClasses: 'items-center lg:gap-2',
    breakpoints: {
      lg: 'items-center gap-2',
      base: 'items-center',
    },
  });

  const rightControlsClass = getClassValue({
    defaultClasses: 'items-center lg:gap-2',
    breakpoints: {
      lg: 'items-center gap-2',
      base: 'items-center',
    },
  });

  const playButtonClass = getClassValue({
    defaultClasses: 'sm:ml-2',
    breakpoints: {
      sm: 'ml-2',
    },
  });

  /**
   * Skip backward 10s.
   */
  const handleSkipBack = () => {
    if (player) {
      const newTime = player.getCurrentTime() - 10;
      handleSeekTo(newTime);
    }
  };

  /**
   * Skip forward 10s.
   */
  const handleSkipForward = () => {
    if (player) {
      const newTime = player.getCurrentTime() + 10;
      handleSeekTo(newTime);
    }
  };

  /**
   * Seek to position.
   */
  const handleChangePosition = (value: number) => {
    handleSeekTo(value);
  };

  /**
   * Get current speed icon.
   */
  const getSpeedIcon = () => {
    if (playbackRate > 2.5) return faGaugeMax;
    if (playbackRate > 1.5) return faGaugeHigh;
    if (playbackRate < 1) return faGaugeLow;

    return faGaugeMed;
  };

  const fullscreenIcon = isFullscreen
    ? faArrowDownLeftAndArrowUpRightToCenter
    : faArrowUpRightAndArrowDownLeftFromCenter;
  return (
    <Flex
      cn={`h-[70px] ${bg1} rounded-b-lg absolute bottom-0 w-ull flex-col gap-1 w-full ${className || ''}`}
    >
      <Box cn="h-5">
        <Box cn="p-2">
          <VideoProgressBar
            duration={player?.getDuration() || 0}
            handleChangePosition={handleChangePosition}
            chapters={chapters}
            progressState={progressState}
          />
        </Box>
      </Box>
      <Flex cn="items-center justify-between">
        <Flex cn={leftControlsClass}>
          <Box cn={playButtonClass}>
            <VideoControlButton
              icon={<Icon icon={playing ? faPause : faPlay} />}
              onClick={handlePlayPause}
              ariaLabel={playing ? 'pause button' : 'play button'}
              data-testid="play-pause-toggle-button"
              tooltipText={intl.formatMessage({
                id: 'video_control_bar.play_pause',
                defaultMessage: 'Play/Pause',
              })}
            />
          </Box>
          <ResponsiveText
            data-testid="video-control-bar-time"
            cn="max-[448px]:hidden"
          >
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {formatMinutesSeconds(player?.getCurrentTime() || 0)} /{' '}
            {formatMinutesSeconds(player?.getDuration() || 0)}
          </ResponsiveText>
          <VideoControlButton
            icon={<SkipBackward />}
            onClick={handleSkipBack}
            tooltipText={intl.formatMessage({
              id: 'video_control_bar.backward_10s',
              defaultMessage: 'Backward 10s',
            })}
            ariaLabel="Backward 10s"
            data-testid="skip-backward-button"
          />
          <VideoControlButton
            icon={<SkipForward />}
            onClick={handleSkipForward}
            tooltipText={intl.formatMessage({
              id: 'video_control_bar.forward_10s',
              defaultMessage: 'Forward 10s',
            })}
            ariaLabel="Forward 10s"
            data-testid="skip-forward-button"
          />
          <ResponsiveBox>
            <VolumeControls />
          </ResponsiveBox>
        </Flex>
        <Flex cn={rightControlsClass}>
          {!isFullscreen && showOAIButton && !railAlwaysOpen ? (
            <OsmosisAIButton
              leftIcon={
                <Icon icon={faSparkles} className={oaiIconClass} size={18} />
              }
              classNames={{
                container: oaiContainerClass,
                button: oaiButtonClass,
                text: oaiTextClass,
              }}
              placement={OaiPlacement.VIDEO}
              source="Learn"
              showLoggedOut
            />
          ) : null}
          {!!chapters?.length && (
            <VideoControlButton
              icon={<Icon icon={faThList} />}
              ariaLabel="Chapters"
              ariaControls="video-chapters"
              tooltipText={intl.formatMessage({
                id: 'video_control_bar.chapters',
                defaultMessage: 'Chapters',
              })}
              data-testid="chapters-button"
              popupMenu={
                <ChapterBrowser
                  chapters={chapters}
                  currentPosition={progressState.playedSeconds}
                  handleChangePosition={handleChangePosition}
                />
              }
              popupMenuStyle={{ right: -150 }}
            />
          )}

          {type !== VideoEnum.YOUTUBE && (
            <VideoControlButton
              onClick={handlePictureInPicture}
              data-testid="picture-in-picture-button"
              ariaLabel="picture-in-picture"
              tooltipText={intl.formatMessage({
                id: 'video_control_bar.Picture_in_picture',
                defaultMessage: 'Picture-in-picture',
              })}
              icon={<Icon icon={faClone} />}
            />
          )}

          <VideoControlButton
            icon={<Icon icon={faClosedCaptioning} />}
            data-testid="caption-select-button"
            shouldCloseMenu={true}
            popupMenu={
              <CaptionSelect
                captions={captions}
                activeCaptions={activeCaptions}
                setActiveCaptions={setActiveCaptions}
              />
            }
            ariaLabel="captions"
            ariaHaspopup={true}
            ariaControls="captions-language-menu"
            tooltipText={intl.formatMessage({
              id: 'video_control_bar.captions',
              defaultMessage: 'Captions',
            })}
          />

          <ResponsiveBox>
            <VideoControlButton
              icon={<Icon icon={faKeyboard} />}
              data-testid="keyboard-shortcuts-button"
              popupMenu={<KeyboardShortcuts shortcuts={shortcuts} />}
              popupMenuStyle={{ right: -100 }}
              ariaLabel="keyboard shortcuts"
              tooltipText={intl.formatMessage({
                id: 'video_control_bar.keyboard_shortcuts',
                defaultMessage: 'Keyboard shortcuts',
              })}
            />
          </ResponsiveBox>

          <VideoControlButton
            icon={<Icon icon={getSpeedIcon()} />}
            data-testid="playback-speed-button"
            shouldCloseMenu={true}
            popupMenu={
              <PlaybackSpeedSelector
                playbackRate={playbackRate}
                setPlaybackRate={setPlaybackRate}
              />
            }
            ariaLabel="playback rate"
            ariaHaspopup={true}
            ariaControls="playback-rate-menu"
            tooltipText={intl.formatMessage({
              id: 'video_control_bar.playback_rate',
              defaultMessage: 'Playback rate',
            })}
          />

          <VideoControlButton
            icon={<Icon icon={fullscreenIcon} />}
            data-testid="fullscreen-button"
            onClick={handleFullscreen}
            ariaLabel="Fullscreen"
            tooltipText={intl.formatMessage({
              id: 'video_control_bar.fullscreen',
              defaultMessage: 'Fullscreen',
            })}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}
